/**
 * CPT Proof Map Panel
 * 
 * Expandable proof map view for Phase 3 reconciliation UI.
 * Shows per-CPT requirement status, evidence snippets with
 * highlights and source attribution, and rule explanations.
 * 
 * Mandatory for compliance defensibility.
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  FileText,
  Mic,
  Search,
  Shield,
  Info,
  MapPin,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  CptProofMapResult,
  RequirementMatchResult,
  ProofEvidenceSnippet,
  ProofDocStatus,
  RequirementStatus,
  RuleConfidence,
  CaseProofMapResult,
} from '@/lib/proofmap/types';

// ============================================================================
// STATUS DISPLAY HELPERS
// ============================================================================

function DocStatusBadge({ status }: { status: ProofDocStatus }) {
  const config = {
    SUFFICIENT: {
      icon: <CheckCircle2 className="w-3.5 h-3.5" />,
      label: 'Sufficient',
      className: 'bg-success/10 text-success border-success/30',
    },
    INSUFFICIENT: {
      icon: <XCircle className="w-3.5 h-3.5" />,
      label: 'Insufficient',
      className: 'bg-destructive/10 text-destructive border-destructive/30',
    },
    AMBIGUOUS: {
      icon: <AlertTriangle className="w-3.5 h-3.5" />,
      label: 'Ambiguous',
      className: 'bg-warning/10 text-warning border-warning/30',
    },
  };
  const c = config[status];
  return (
    <Badge variant="outline" className={cn('flex items-center gap-1 text-xs', c.className)}>
      {c.icon}
      {c.label}
    </Badge>
  );
}

function ReqStatusIcon({ status }: { status: RequirementStatus }) {
  switch (status) {
    case 'SATISFIED':
      return <CheckCircle2 className="w-4 h-4 text-success" />;
    case 'NOT_FOUND':
      return <XCircle className="w-4 h-4 text-destructive" />;
    case 'AMBIGUOUS':
      return <AlertTriangle className="w-4 h-4 text-warning" />;
  }
}

function ConfidenceBadge({ confidence }: { confidence: RuleConfidence | null }) {
  if (!confidence) return null;
  const colors = {
    HIGH: 'bg-success/10 text-success border-success/20',
    MEDIUM: 'bg-warning/10 text-warning border-warning/20',
    LOW: 'bg-muted text-muted-foreground border-border',
  };
  return (
    <Badge variant="outline" className={cn('text-[10px] font-normal', colors[confidence])}>
      {confidence}
    </Badge>
  );
}

function SourceBadge({ source }: { source: 'DICTATION' | 'OP_NOTE' }) {
  return (
    <Badge variant="outline" className="text-[10px] gap-1 font-normal">
      {source === 'DICTATION' ? (
        <Mic className="w-2.5 h-2.5" />
      ) : (
        <FileText className="w-2.5 h-2.5" />
      )}
      {source === 'DICTATION' ? 'Dictation' : 'Op Note'}
    </Badge>
  );
}

// ============================================================================
// EVIDENCE SNIPPET COMPONENT
// ============================================================================

function EvidenceSnippetCard({ snippet }: { snippet: ProofEvidenceSnippet }) {
  return (
    <div className={cn(
      'p-2.5 rounded-md border text-xs',
      snippet.is_negation
        ? 'border-destructive/30 bg-destructive/5'
        : 'border-success/30 bg-success/5'
    )}>
      <div className="flex items-center gap-2 mb-1.5">
        <SourceBadge source={snippet.source} />
        {snippet.section_hint && (
          <Badge variant="outline" className="text-[10px] gap-1 font-normal">
            <MapPin className="w-2.5 h-2.5" />
            {snippet.section_hint}
          </Badge>
        )}
        {snippet.is_negation && (
          <Badge variant="outline" className="text-[10px] bg-destructive/10 text-destructive border-destructive/20">
            Negation
          </Badge>
        )}
      </div>
      <p className="font-mono text-[11px] leading-relaxed text-foreground/80 italic">
        "…{snippet.matched_text}…"
      </p>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {snippet.matched_patterns.map((p, i) => (
          <span key={i} className="px-1.5 py-0.5 bg-primary/10 text-primary rounded text-[10px] font-medium">
            {p}
          </span>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// REQUIREMENT ROW COMPONENT
// ============================================================================

function RequirementRow({ result }: { result: RequirementMatchResult }) {
  const [isOpen, setIsOpen] = useState(result.status !== 'SATISFIED');
  const snippetCount = result.all_snippets.length;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center gap-3 py-2 px-3 rounded-md hover:bg-muted/50 transition-colors w-full text-left">
          <ReqStatusIcon status={result.status} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">{result.label}</span>
              <Badge variant="outline" className="text-[10px] font-normal">
                {result.strictness}
              </Badge>
              <ConfidenceBadge confidence={result.best_confidence} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            {snippetCount > 0 && (
              <span className="text-xs text-muted-foreground">
                {snippetCount} snippet{snippetCount !== 1 ? 's' : ''}
              </span>
            )}
            {result.has_contradiction && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <AlertTriangle className="w-3.5 h-3.5 text-warning" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">Contradiction: both positive and negative evidence found</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {isOpen ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-7 pl-3 border-l-2 border-muted space-y-2 pb-3">
          {/* Explanation */}
          <p className="text-xs text-muted-foreground">
            {result.explanation}
          </p>

          {/* Evidence snippets */}
          {result.all_snippets.length > 0 ? (
            <div className="space-y-2">
              {result.all_snippets.map((snippet, i) => (
                <EvidenceSnippetCard key={i} snippet={snippet} />
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground italic">
              No evidence snippets found in source documents.
            </p>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ============================================================================
// CPT PROOF MAP CARD
// ============================================================================

function CptProofCard({ result }: { result: CptProofMapResult }) {
  const [isOpen, setIsOpen] = useState(
    result.documentation_status !== 'SUFFICIENT'
  );

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30 transition-colors w-full text-left">
          <div className="flex items-center gap-3">
            <Badge className="font-mono text-xs">{result.cpt_code}</Badge>
            <span className="text-sm text-muted-foreground truncate max-w-[300px]">
              {result.cpt_description}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <DocStatusBadge status={result.documentation_status} />
            <span className="text-xs text-muted-foreground">
              {result.satisfied_count}/{result.total_requirements}
            </span>
            {result.has_any_contradiction && (
              <AlertTriangle className="w-4 h-4 text-warning" />
            )}
            {isOpen ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 ml-2 space-y-1">
          {result.requirement_results.map(req => (
            <RequirementRow key={req.requirement_id} result={req} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ============================================================================
// MAIN PANEL
// ============================================================================

interface CptProofMapPanelProps {
  proofMapResult: CaseProofMapResult | null;
  isAnalyzing?: boolean;
  isDemoMode?: boolean;
}

export function CptProofMapPanel({
  proofMapResult,
  isAnalyzing = false,
  isDemoMode = false,
}: CptProofMapPanelProps) {
  if (isAnalyzing) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Search className="w-4 h-4 text-primary animate-pulse" />
            Building Proof Map…
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-24 flex items-center justify-center">
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <div className="w-2 h-2 rounded-full bg-primary animate-bounce" />
              <div className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:150ms]" />
              <div className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:300ms]" />
              <span>Matching evidence rules against documentation…</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!proofMapResult) return null;

  const { cpt_results, sufficient_count, insufficient_count, ambiguous_count, total_cpts, sources_used } = proofMapResult;

  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="w-5 h-5 text-primary" />
            CPT Proof Map
            {isDemoMode && (
              <Badge variant="secondary" className="text-xs ml-1">Demo</Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            {sources_used.map(s => (
              <SourceBadge key={s} source={s} />
            ))}
          </div>
        </div>
        <CardDescription className="text-xs">
          Rule-based evidence mapping — every match is traceable to surgeon-spoken words or documentation
        </CardDescription>

        {/* Summary bar */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-1.5 text-xs">
            <CheckCircle2 className="w-3.5 h-3.5 text-success" />
            <span className="font-medium text-success">{sufficient_count}</span>
            <span className="text-muted-foreground">Sufficient</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <XCircle className="w-3.5 h-3.5 text-destructive" />
            <span className="font-medium text-destructive">{insufficient_count}</span>
            <span className="text-muted-foreground">Insufficient</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <AlertTriangle className="w-3.5 h-3.5 text-warning" />
            <span className="font-medium text-warning">{ambiguous_count}</span>
            <span className="text-muted-foreground">Ambiguous</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs ml-auto">
            <span className="text-muted-foreground">{total_cpts} CPTs evaluated</span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {cpt_results.map(result => (
          <CptProofCard key={result.cpt_code} result={result} />
        ))}

        {/* Compliance notice */}
        <Alert className="border-muted bg-muted/20">
          <Info className="w-4 h-4" />
          <AlertDescription className="text-xs">
            <span className="font-semibold">Compliance:</span> All evidence is matched using 
            deterministic, human-defined rules. LINK does not infer clinical facts or suggest procedures. 
            Every satisfied requirement is traceable to explicit text in the operative documentation.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}
